import React from 'react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { LinkedReference, Reference } from '@1po/1po-bff-fe-spec/generated/estimate/response/model/Estimate';
import { v4 } from 'uuid';
import { RootState } from 'app/AppStore';
import { useIsB2B } from 'components/B2BComponents/useIsB2B';
import {
  getBundlesOrRecycling,
  getBundlesOrRecyclingWithValidPrices,
  getEstimateBundleOrRecyclingSectionPriceVatExcl,
  getEstimateBundleOrRecyclingSectionVat,
  getEstimateClient,
  getEstimateCreatedBy,
  getEstimateLaborTimeSectionPriceVatExcl,
  getEstimateLaborTimeSectionVat,
  getEstimateObservations,
  getEstimateOperationLaborTimesSectionPriceVatExcl,
  getEstimateOperationLaborTimesSectionVat,
  getEstimateOperationReferenceSectionPriceVatExcl,
  getEstimateOperationReferencesVat,
  getEstimateReferenceSectionPriceVatExcl,
  getEstimateReferenceSectionVat,
  getEstimateSequenceNumber,
  getEstimateSettings,
  getEstimateTotalPrice,
  getEstimateVehicle,
  getLaborTimes,
  getLaborTimesWithValidPrices,
  getOperationsWithInvalidPrices,
  getOperationsWithValidPrices,
  getReferences,
  getReferencesWithValidPrices,
  getValidRowTablesCount,
} from 'domains/estimate/Estimate.store';
import { getUserGarageInfo } from 'domains/garage/Garage.store';
import { getCurrency, getUserCommercialLink } from 'domains/user';
import { useEprelPdfImages } from 'pages/EstimatePage/useEprelPdfImages';
import { useFetchEprelBase64 } from 'pages/EstimatePage/useFetchEprelBase64';
import { EstimatePdfData, EstimatePdfReference } from 'pages/EstimatePdf';
import { getData } from 'utils';

function useGetOnlyValidEstimateData(estimateId: string) {
  const validReferenceData = useSelector((state: RootState) => getReferencesWithValidPrices(state, estimateId));
  const validReferencesWithLinked = validReferenceData.reduce((refs: (Reference | LinkedReference)[], ref) => {
    if (ref.linkedReferences) {
      return [...refs, ref, ...ref.linkedReferences];
    }
    return [...refs, ref];
  }, []);

  const validLaborTimeData = useSelector((state: RootState) => getLaborTimesWithValidPrices(state, estimateId));
  const validBundlesOrRecycling = useSelector((state: RootState) =>
    getBundlesOrRecyclingWithValidPrices(state, estimateId),
  );
  const validOperations = useSelector((state: RootState) => getOperationsWithValidPrices(state, estimateId));

  return {
    validReferencesWithLinked,
    validLaborTimeData,
    validBundlesOrRecycling,
    validOperations,
  };
}

function useGetInvalidIncludedEstimateData(estimateId: string) {
  const invalidReferenceData = useSelector((state: RootState) => getReferences(state, estimateId));
  const invalidReferencesWithLinked = invalidReferenceData.reduce((refs: (Reference | LinkedReference)[], ref) => {
    if (ref.linkedReferences) {
      return [...refs, ref, ...ref.linkedReferences];
    }
    return [...refs, ref];
  }, []);
  const invalidLaborTimeData = useSelector((state: RootState) => getLaborTimes(state, estimateId));
  const invalidBundlesOrRecycling = useSelector((state: RootState) => getBundlesOrRecycling(state, estimateId));
  const invalidOperations = useSelector((state: RootState) => getOperationsWithInvalidPrices(state, estimateId));
  return {
    invalidReferencesWithLinked,
    invalidLaborTimeData,
    invalidBundlesOrRecycling,
    invalidOperations,
  };
}

export function useIsWholeEstimateDataValid(estimateId: string) {
  const { validReferencesWithLinked, validLaborTimeData, validBundlesOrRecycling } = useGetOnlyValidEstimateData(
    estimateId,
  );
  const {
    invalidReferencesWithLinked,
    invalidLaborTimeData,
    invalidBundlesOrRecycling,
    invalidOperations,
  } = useGetInvalidIncludedEstimateData(estimateId);
  const numberOfItems = useSelector((state: RootState) => getValidRowTablesCount(state, estimateId));
  const isAllOperationsValid = invalidOperations.every((op) => {
    return (
      op.referenceSubsection.references.length === op.referenceSubsection.totalValidItems &&
      op.laborTimeSubsection.laborTimes.length === op.laborTimeSubsection.totalValidItems
    );
  });

  return (
    validReferencesWithLinked.length === invalidReferencesWithLinked.length &&
    validLaborTimeData.length === invalidLaborTimeData.length &&
    validBundlesOrRecycling.length === invalidBundlesOrRecycling.length &&
    isAllOperationsValid &&
    numberOfItems >= 1
  );
}

export function useEstimatePdfData(
  estimateId: string,
  includeInvalidItems: boolean,
): { data: EstimatePdfData; isEprelReady: boolean } {
  const currency = useSelector(getCurrency);
  const commercialLink = useSelector(getUserCommercialLink);
  const isB2B = useIsB2B();
  const createdBy = useSelector((state: RootState) => getEstimateCreatedBy(state, estimateId));

  const getGarageName = () => (isB2B ? commercialLink?.name : commercialLink?.seller?.name);
  const r1Dealer = {
    garageName: getGarageName() ?? '',
    createdBy: createdBy?.creatorName ?? '',
    tel: '',
    email: '',
  };

  const {
    validReferencesWithLinked,
    validLaborTimeData,
    validBundlesOrRecycling,
    validOperations,
  } = useGetOnlyValidEstimateData(estimateId);
  const {
    invalidReferencesWithLinked,
    invalidLaborTimeData,
    invalidBundlesOrRecycling,
    invalidOperations,
  } = useGetInvalidIncludedEstimateData(estimateId);

  const referencesWithLinked = includeInvalidItems ? invalidReferencesWithLinked : validReferencesWithLinked;
  const operations = includeInvalidItems ? invalidOperations : validOperations;
  const laborTimeData = includeInvalidItems ? invalidLaborTimeData : validLaborTimeData;
  const bundlesOrRecycling = includeInvalidItems ? invalidBundlesOrRecycling : validBundlesOrRecycling;

  const referencesEprelUrls = referencesWithLinked
    .map((ref) => ('eprelUrl' in ref ? ref.eprelUrl : undefined))
    .filter((url): url is string => Boolean(url));
  useFetchEprelBase64(referencesEprelUrls);
  // Resolve EPREL payloads to ready-to-render image URIs in the DOM tree, before pdf(...).toBlob().
  const { images: eprelImages, isReady: isEprelReady } = useEprelPdfImages(referencesEprelUrls);
  const referencesWithEprel: EstimatePdfReference[] = referencesWithLinked.map((ref) => ({
    ...ref,
    eprel: 'eprelUrl' in ref && ref.eprelUrl ? eprelImages.get(ref.eprelUrl) : undefined,
  }));

  const numberOfItems = useSelector((state: RootState) => getValidRowTablesCount(state, estimateId));
  const priceDetail = useSelector((state: RootState) => getEstimateTotalPrice(state, estimateId));

  const vehicleDetail = useSelector((state: RootState) => getEstimateVehicle(state, estimateId));
  const client = useSelector((state: RootState) => getEstimateClient(state, estimateId));

  const garageInfo = useSelector(getUserGarageInfo);
  const garageInfoData = getData(garageInfo);

  const referencesSectionPriceVatExcl = Number(
    useSelector((state: RootState) => getEstimateReferenceSectionPriceVatExcl(state, estimateId)),
  );
  const referenceOperationSectionPriceVatExcl = Number(
    useSelector((state: RootState) => getEstimateOperationReferenceSectionPriceVatExcl(state, estimateId)),
  );
  const referencesPriceVatExcl = referencesSectionPriceVatExcl + referenceOperationSectionPriceVatExcl;
  const referencesSectionVat = Number(
    useSelector((state: RootState) => getEstimateReferenceSectionVat(state, estimateId)),
  );
  const referenceOperationSectionVat = Number(
    useSelector((state: RootState) => getEstimateOperationReferencesVat(state, estimateId)),
  );
  const referencesVat = referencesSectionVat + referenceOperationSectionVat;
  const bundleOrRecyclingPriceVatExcl = useSelector((state: RootState) =>
    getEstimateBundleOrRecyclingSectionPriceVatExcl(state, estimateId),
  );
  const bundleOrRecyclingVat = useSelector((state: RootState) =>
    getEstimateBundleOrRecyclingSectionVat(state, estimateId),
  );
  const laborTimeSectionPriceVatExcl = Number(
    useSelector((state: RootState) => getEstimateLaborTimeSectionPriceVatExcl(state, estimateId)),
  );
  const laborTimeOperationSectionPriceVatExcl = Number(
    useSelector((state: RootState) => getEstimateOperationLaborTimesSectionPriceVatExcl(state, estimateId)),
  );
  const laborTimePriceVatExcl = laborTimeSectionPriceVatExcl + laborTimeOperationSectionPriceVatExcl;
  const laborTimeSectionVat = Number(
    useSelector((state: RootState) => getEstimateLaborTimeSectionVat(state, estimateId)),
  );
  const laborTimeOperationSectionVat = Number(
    useSelector((state: RootState) => getEstimateOperationLaborTimesSectionVat(state, estimateId)),
  );
  const laborTimeVat = laborTimeSectionVat + laborTimeOperationSectionVat;
  const sequenceNumber = useSelector((state: RootState) => getEstimateSequenceNumber(state, estimateId));
  const estimateObservations = useSelector((state: RootState) => getEstimateObservations(state, estimateId));
  const settings = useSelector(getEstimateSettings);
  const settingsData = getData(settings);
  const intraCommunityVat = settingsData?.intraCommunityVat;
  const estimateValidFor = settingsData?.estimateValidFor;
  const validityUnit = settingsData?.validityUnit;

  const legalMention = settingsData?.legalMentions;
  const observation = sequenceNumber ? estimateObservations : settingsData?.observations;
  const text = {
    title: <Trans i18nKey="estimate.pdf.estimate">Estimate</Trans>,
    reference: <Trans i18nKey="estimate.pdf.reference">Reference</Trans>,
    eprel: <Trans i18nKey="catalog.tires.eprel">EPREL</Trans>,
    designation: <Trans i18nKey="estimate.pdf.designation">Designation</Trans>,
    unit_price: <Trans i18nKey="estimate.pdf.unit_price">Unit price</Trans>,
    quantity: <Trans i18nKey="estimate.pdf.quantity">Quantity</Trans>,
    discount: <Trans i18nKey="estimate.pdf.discount">Discount</Trans>,
    price_vat_excl: <Trans i18nKey="estimate.pdf.price_vat_excl">Price VAT Excl.</Trans>,
    vat: <Trans i18nKey="estimate.pdf.vat">VAT</Trans>,
    unit_price_rate: <Trans i18nKey="estimate.pdf.unit_price_rate">Unit Price - Rate</Trans>,
    code: <Trans i18nKey="estimate.pdf.code">Code</Trans>,
  };

  const key = v4();

  const data: EstimatePdfData = {
    reference: referencesWithEprel,
    laborTime: laborTimeData,
    date: new Date(),
    dealer: garageInfoData,
    orderDetails: {
      reference: { priceVatExcl: referencesPriceVatExcl.toString(), vat: referencesVat.toString() },
      laborTime: { priceVatExcl: laborTimePriceVatExcl.toString(), vat: laborTimeVat.toString() },
      bundlesOrRecycling: { priceVatExcl: bundleOrRecyclingPriceVatExcl, vat: bundleOrRecyclingVat },
    },
    price: priceDetail,
    observation: observation ?? '',
    operations,
    sequenceNumber,
    r1Dealer,
    bundlesOrRecycling,
    vehicleDetail,
    currency,
    client,
    numberOfItems,
    intraCommunityVat,
    estimateValidFor,
    validityUnit,
    legalMention,
    key,
    text,
  };

  return { data, isEprelReady };
}
